/* eslint-disable no-unused-vars */
import { useSelector } from 'react-redux';
import { useSignal, useComputed } from '@preact/signals-react';
import { TableCell, ParagraphWithTooltip } from 'components';
import { cellWidth, campaignReportsDefaultSelectedCols } from 'constants';

import * as S from './RightSideHeader.styled';

function getECPM(impressions = 0, cost = 0) {
  const ecpm = (+cost / +impressions) * 1000;
  if (impressions > 100 && !Number.isNaN(ecpm)) return ecpm.toFixed(3);
  return 0;
}

export const RightSideHeader = ({ data$, sortConfig$, isUa, isNoData$ }) => {
  const allColls = campaignReportsDefaultSelectedCols.cols.map((col) => col.colName);
  const selectedCols = useSelector((state) => state.campaignReports.selectedCols);
  const selectedColLabels = useSelector((state) => state.campaignReports.selectedColLabels);
  const selectedEvents = useSelector((state) => state.campaignReports.selectedEvents);
  const currentRequestType = useSelector((state) => state.campaignReports.tableRequestTypes);

  const bidType = data$.value?.[0]?.bid_type;

  // calculate all sums
  const sumObj$ = useComputed(() => {
    let sumObj = {};
    // let crCount = 0;
    data$.value?.forEach((item) => {
      allColls.forEach((metric, ind) => {
        if (metric !== 'AvgCpi' && metric !== 'CR' && metric !== 'CTR') {
          sumObj[metric] = sumObj[metric] ? sumObj[metric] + item[metric] : +item[metric];
        }
        // if (metric === 'CR' && item[metric] !== 0) {
        //   crCount++;
        // }
      });
    });

    sumObj.AvgCpi = sumObj.Installs ? sumObj.Cost / sumObj.Installs : 0;
    sumObj.CR = sumObj.Clicks ? (sumObj.Installs / sumObj.Clicks) * 100 : 0;
    sumObj.CTR = sumObj.Impressions ? (sumObj.Clicks / sumObj.Impressions) * 100 : 0;

    sumObj.eCPM = getECPM(sumObj.Impressions, sumObj.Cost);

    return sumObj;
  });

  // calculate all event sums
  const events$ = useComputed(() => {
    let eventsMap = new Map();
    const eventIds = data$.value?.[0]?.event_id_arr; // data.eventIds;

    data$.value?.forEach((item) => {
      eventIds?.forEach((id, ind) => {
        if (eventsMap.has(id)) {
          const newValue = {
            id,
            name: eventsMap.get(id).name,
            value: eventsMap.get(id).value + item.eventCountArr[ind],
            valueCohort: eventsMap.get(id).valueCohort + item.eventCountArrCohort[ind],
          };
          eventsMap.set(id, newValue);
        } else {
          eventsMap.set(id, {
            id,
            name: item?.event_name_arr?.[ind],
            value: item?.eventCountArr?.[ind],
            valueCohort: item?.eventCountArrCohort?.[ind],
          });
        }
      });
    });

    return eventsMap;
  });

  // filter all total values to display only selected
  const totalSums = selectedCols.map((metric) => formateValue(sumObj$.value[metric], metric));
  const totalEventSums = selectedEvents
    .filter((item) => item.isSelected)
    .sort((a, b) => b.orderId - a.orderId)
    .map((item) => events$.value.get(item.eventId))
    .filter((item) => item); // to handle 'no data' case

  const requestSort = (key) => {
    let direction = 'descending';
    if (
      sortConfig$.value &&
      sortConfig$.value.key === key &&
      sortConfig$.value.direction === 'descending'
    ) {
      direction = 'ascending';
    }
    sortConfig$.value = { key, direction };
  };

  const getClassNamesFor = (name) => {
    if (!sortConfig$.value) return;
    return sortConfig$.value.key === name ? sortConfig$.value.direction : undefined;
  };

  const isBidBorder = !(selectedCols.length === 0 && events$.value?.size > 0);

  const twoLinesTitleStyles = {
    whiteSpace: 'break-spaces',
    width: 'min-content',
    textAlign: 'center',
  };

  return (
    <>
      <S.HeaderTitles>
        {currentRequestType === 'Apps' && (
          <TableCell.Component
            value={'cm_app_cpi'}
            onClick={requestSort}
            direction={getClassNamesFor('cm_app_cpi')}
            style={{
              width: cellWidth.bid,
              borderRight: `${isBidBorder ? '1px solid #e2e8f0' : 'none'}`,
              justifyContent: 'start',
            }}
          >
            Bid
          </TableCell.Component>
        )}
        {selectedCols.map((cellName, ind) => (
          <TableCell.Component
            value={cellName}
            key={cellName}
            onClick={requestSort}
            direction={getClassNamesFor(cellName)}
            style={{
              width: getCollWidth(cellName),
              marginRight: ind === selectedCols.length - 1 ? '8px' : 0,
            }}
          >
            <ParagraphWithTooltip
              style={{
                maxWidth: `${getWrapWidth(getCollWidth(cellName))}`,
                ...(cellName === 'Impressions' ? twoLinesTitleStyles : {}),
              }}
              text={selectedColLabels[ind]}
            />
          </TableCell.Component>
        ))}
        {totalEventSums?.length > 0 && (
          <>
            <S.EventsWrap>
              <S.EventsTitle style={{ minWidth: cellWidth.event }}>Events</S.EventsTitle>
              <S.EventList>
                {totalEventSums.map((item, ind) => (
                  <TableCell.Component
                    value={'events_' + item.id}
                    key={item.id + ind}
                    onClick={requestSort}
                    direction={getClassNamesFor('events_' + item.id)}
                    style={eventTitleStyles}
                  >
                    <ParagraphWithTooltip
                      style={{ maxWidth: `${getWrapWidth(cellWidth.event)}` }}
                      text={item.name}
                    />
                  </TableCell.Component>
                ))}
              </S.EventList>
            </S.EventsWrap>
            <S.EventsWrap>
              <S.EventsTitle style={{ minWidth: cellWidth.event }}>Events (Cohort)</S.EventsTitle>
              <S.EventList>
                {totalEventSums.map((item, ind) => (
                  <TableCell.Component
                    value={'eventsCohort_' + item.id}
                    key={item.id + ind + 'cohort'}
                    onClick={requestSort}
                    direction={getClassNamesFor('eventsCohort_' + item.id)}
                    style={eventTitleStyles}
                  >
                    <S.TextWrap style={{ maxWidth: `${getWrapWidth(cellWidth.event)}` }}>
                      <ParagraphWithTooltip
                        style={{ maxWidth: `${getWrapWidth(cellWidth.event)}` }}
                        text={item.name}
                      />
                    </S.TextWrap>
                  </TableCell.Component>
                ))}
              </S.EventList>
            </S.EventsWrap>
          </>
        )}
      </S.HeaderTitles>
      {!isNoData$.value ? (
        <S.HeaderSums>
          {currentRequestType === 'Apps' && <div style={{ width: cellWidth.bid }}></div>}
          {totalSums?.map((item, ind) => (
            <S.Sum
              key={item + ind}
              style={{
                width: getCollWidth(selectedCols[ind]),
                marginRight: ind === totalSums.length - 1 ? '8px' : 0,
                justifyContent: 'center',
              }}
            >
              <S.SumInner>{item}</S.SumInner>
            </S.Sum>
          ))}
          {totalEventSums?.length > 0 && (
            <>
              <S.EventSums>
                {totalEventSums.map((item, ind) => (
                  <S.EventSum
                    key={'eventsum' + ind}
                    style={{
                      width: cellWidth.event,
                    }}
                  >
                    <div>{formatEventValue(item.value)}</div>
                    <div>
                      ({calcPercentageOfEvents(item.value, sumObj$.value, bidType, isUa)}%, $
                      {calcCPAOfEvents(item.value, sumObj$.value.Cost)})
                    </div>
                  </S.EventSum>
                ))}
              </S.EventSums>
              <S.EventSums>
                {totalEventSums.map((item, ind) => (
                  <S.EventSum
                    key={'eventcohortsum' + ind}
                    style={{
                      width: cellWidth.event,
                    }}
                  >
                    <div>{formatEventValue(item.valueCohort)}</div>
                    <div>
                      ({calcPercentageOfEvents(item.valueCohort, sumObj$.value, bidType, isUa)}%, $
                      {calcCPAOfEvents(item.valueCohort, sumObj$.value.Cost)})
                    </div>
                  </S.EventSum>
                ))}
              </S.EventSums>
            </>
          )}
        </S.HeaderSums>
      ) : null}
    </>
  );
};

const formatEventValue = (value) => {
  if (!value) return 0;
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const eventTitleStyles = {
  width: cellWidth.event,
  height: '100%',
  padding: '4px',
};

function calcPercentageOfEvents(eventValue, sum, bidType, isUa) {
  let value = 0;

  if (bidType == 2) {
    value = isUa ? sum.Installs : sum.Clicks; // CPC
  } else {
    value = sum.Installs; // CPI, CPM
  }

  return calcPercentage(eventValue, value);
}

function getWrapWidth(string) {
  return string.replace('px', '') - 18 + 'px';
}

function getCollWidth(metric) {
  return cellWidth[metric] || cellWidth.base;
}

function formateValue(val, metric) {
  if (!val) val = 0;

  let pref = '';
  let suf = '';

  if (metric === 'Cost' || metric === 'AvgCpi' || metric === 'eCPM') pref = '$';
  if (metric === 'CR' || metric === 'CTR') suf = '%';

  const minfractionDigits = metric === 'Cost' && !!val ? 2 : metric === 'eCPM' ? 3 : 0;
  const maxfractionDigits = metric === 'eCPM' ? 3 : 2;

  const formattedNumber = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: minfractionDigits,
    maximumFractionDigits: maxfractionDigits,
  }).format(+val);

  return pref + formattedNumber + suf;
}

function calcCPAOfEvents(eventValue, cost) {
  const cpa = eventValue ? cost / eventValue : 0;
  return +cpa.toFixed(2);
}

function calcPercentage(n1, n2) {
  return n2 === 0 ? '0' : ((n1 / n2) * 100).toFixed(0);
}
